import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut } from 'lucide-react';
import { mockData } from '../mock';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Feature flag — set REACT_APP_MARKETPLACE_ENABLED=false to hide the
  // public Used Cars section (marketplace + Sell-Your-Car CTAs). Admin
  // side keeps working so ops can still curate inventory in the
  // background. Flip to `true` (or remove the env var) to re-enable.
  const marketplaceEnabled = String(process.env.REACT_APP_MARKETPLACE_ENABLED ?? 'true').toLowerCase() !== 'false';

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Book', href: '/book' },
    ...(marketplaceEnabled ? [{ name: 'Used Cars', href: '/marketplace' }] : []),
    { name: 'Training', href: '/training' },
    { name: 'PPF Story', href: '/ppf' },
    { name: 'eWarranty', href: '/e-warranty' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'Testimonials', href: '/testimonials' },
    { name: 'Blog', href: '/blog' },
    { name: 'Driveline', href: '/driveline' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg border-b border-gray-200' : 'bg-white'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-2">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_auto-care-site/artifacts/x6w88o7g_fe093ca0-c03a-4835-8f7e-efa7cf14a851.jpeg" 
              alt="Unisol Global Logo"
              className="h-24 lg:h-28 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-6 items-center">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium transition-all duration-200 hover:text-teal-600 ${
                  location.pathname === item.href
                    ? 'text-teal-600 border-b-2 border-teal-600 pb-1'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-2">
            {user && user.role === 'customer' ? (
              <>
                <Link to="/my-listings"
                  className="text-sm font-medium text-slate-700 hover:text-teal-700 px-3 py-2 rounded-lg inline-flex items-center gap-1.5"
                  data-testid="nav-my-listings">
                  <User size={14} /> My listings
                </Link>
                <button onClick={logout}
                  className="text-sm text-slate-500 hover:text-rose-600 p-2"
                  title="Sign out" data-testid="nav-logout">
                  <LogOut size={16} />
                </button>
              </>
            ) : !user ? (
              <Link to="/login"
                className="text-sm font-medium text-slate-700 hover:text-teal-700 px-3 py-2 rounded-lg"
                data-testid="nav-signin">
                Sign in
              </Link>
            ) : null}
            <Link
              to={marketplaceEnabled && (location.pathname.startsWith('/marketplace') || user?.role === 'customer') ? '/sell-your-car' : '/contact'}
              className="bg-teal-600 text-white px-5 py-2 rounded-lg font-semibold hover:bg-teal-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 text-sm"
              data-testid="nav-cta"
            >
              {marketplaceEnabled && (location.pathname.startsWith('/marketplace') || user?.role === 'customer') ? 'Sell Your Car' : 'Get Quote'}
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 py-4 bg-white">
            <div className="flex flex-col space-y-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-base font-medium transition-colors ${
                    location.pathname === item.href
                      ? 'text-teal-600'
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <Link
                to={marketplaceEnabled && (location.pathname.startsWith('/marketplace') || user?.role === 'customer') ? '/sell-your-car' : '/contact'}
                onClick={() => setIsMenuOpen(false)}
                className="bg-teal-600 text-white px-6 py-2 rounded-lg font-semibold text-center mt-4"
                data-testid="mobile-nav-cta"
              >
                {marketplaceEnabled && (location.pathname.startsWith('/marketplace') || user?.role === 'customer') ? 'Sell Your Car' : 'Get Quote'}
              </Link>
              {marketplaceEnabled && user && user.role === 'customer' && (
                <>
                  <Link to="/my-listings" onClick={() => setIsMenuOpen(false)}
                    className="text-base font-medium text-slate-700 mt-2"
                    data-testid="mobile-nav-my-listings">
                    My listings
                  </Link>
                  <button onClick={() => { logout(); setIsMenuOpen(false); }}
                    className="text-base font-medium text-rose-600 text-left mt-2"
                    data-testid="mobile-nav-logout">
                    Sign out
                  </button>
                </>
              )}
              {!user && (
                <Link to="/login" onClick={() => setIsMenuOpen(false)}
                  className="text-base font-medium text-slate-700 mt-2"
                  data-testid="mobile-nav-signin">
                  Sign in
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;